import React, { lazy, Suspense } from 'react';
import { CardBody } from 'reactstrap';
import Select from 'react-select';
import CollapsibleCard from '../CollapsibleCard';
import styles from './BalanceCard.module.scss';
import dollarFormatter from './dollar-formatter';
import Loader from '../../../components/Loader';
import styleVariables from '../../../styles/variables.scss';

const BalanceChart = lazy(() => import('./BalanceChart'));

function DaysButton({ days, lastNDays, setLastNDays }) {
  return (
    <button
      type="button"
      className={`w-100 btn ${days === lastNDays ? 'btn-primary' : 'btn-outline-primary '}`}
      onClick={() => setLastNDays(days)}
    >
      {days}
    </button>
  );
}

function SummaryStat({ title, value }) {
  return (
    <h5>
      {title}
      <br />
      {dollarFormatter(value, true)}
    </h5>
  );
}

export default function ({ balanceData, accounts }) {
  const accountOptions = [
    {
      value: 'all',
      label: 'All Accounts',
      endOfDayBalances: balanceData.dailyAggregatedBalances,
    },
    ...accounts.map((account) => ({
      value: account.accountNO,
      label: `${account.type} ${account.accountNO}`,
      endOfDayBalances: account.endOfDayBalances,
    })),
  ];

  const [selectedAccount, setSelectedAccount] = React.useState(accountOptions.find((option) => option.value === 'all'));
  const [lastNDays, setLastNDays] = React.useState(365);

  const {
    currentBalance,
    allDeposits,
  } = balanceData;
  const dailyAggregatedBalancesArray = Object.entries(selectedAccount.endOfDayBalances).sort().slice(-lastNDays);
  const totalDepositsArray = Object.entries(allDeposits).sort().slice(-lastNDays);
  const averageBalance = dailyAggregatedBalancesArray.reduce(
    (acc, [, balance]) => acc + balance,
    0,
  ) / dailyAggregatedBalancesArray.length;
  const totalDeposits = Object.values(totalDepositsArray).reduce(
    (acc, [, deposit]) => acc + deposit,
    0,
  );

  return (
    <CollapsibleCard title="Banking Balance" collapseKey="bankingBalance">
      <CardBody>
        <div className={`row ${styles.topDiv}`}>
          <div className="col-4">
            <SummaryStat
              title="Current Balance:"
              value={currentBalance}
            />
          </div>
          <div className="col-4">
            <SummaryStat
              title={`Average Balance (${lastNDays}\xa0days):`}
              value={averageBalance}
            />
          </div>
          <div className="col-4">
            <SummaryStat
              title={`Total Deposits (${lastNDays}\xa0days):`}
              value={totalDeposits}
            />
          </div>
        </div>
        <div className="row mx-4 mt-2">
          <div className="col-10 pr-0">
            <Suspense fallback={<Loader />}>
              <div className="mx-n5 mt-n5">
                <BalanceChart
                  lastNDays={lastNDays}
                  dailyAggregatedBalancesArray={dailyAggregatedBalancesArray}
                />
              </div>
            </Suspense>
          </div>
          <div className="col-2 pl-0">
            <h6 className="mt-2">Account:</h6>
            <Select
              value={selectedAccount}
              options={accountOptions}
              theme={(theme) => ({
                ...theme,
                colors: {
                  ...theme.colors,
                  primary25: styleVariables.primaryColor,
                  primary: styleVariables.primaryColor,
                },
              })}
              styles={{
                option: (defaultStyles, { isFocused, isSelected }) => (
                  { ...defaultStyles, color: (isFocused || isSelected) ? '#fff' : null }
                ),
              }}
              onChange={(option) => setSelectedAccount(option)}
            />
            <h6 className="mt-4">Number of Days:</h6>
            <DaysButton
              days={30}
              lastNDays={lastNDays}
              setLastNDays={setLastNDays}
            />
            <DaysButton
              days={90}
              lastNDays={lastNDays}
              setLastNDays={setLastNDays}
            />
            <DaysButton
              days={365}
              lastNDays={lastNDays}
              setLastNDays={setLastNDays}
            />
          </div>
        </div>
      </CardBody>
    </CollapsibleCard>
  );
}
